import CryptoJS from "crypto-js";
import { useContext } from "react";
import {
  endoso as endosoService,
  searchPoliza as searchPolizaService,
} from "../../../../services";
import { CancelacionMasivaContext } from "../../context";

export const errorText = {
  sinArchivo: "Debe cargar el archivo de pólizas para continuar",
  invalidType:
    "El archivo no tiene una extensión permitida, por favor verifique.",
  upload: "Ocurrió un error al cargar el archivo de pólizas",
  previo: "Ocurrió un error al guardar el borrador",
  sinFilas: "No hay pólizas válidas para cancelar",
  ejecutar: "Ocurrió un error al ejecutar la cancelación masiva",
  bloquea: "No se pudo bloquear la póliza",
  guarda: "No se pudo guardar el endoso de la póliza",
  confirma: "No se pudo confirmar el endoso de la póliza",
};

/* el backend responde codigo unas veces como number y otras como string
   (rulesBy-16 compara contra "1" en una linea y contra 1 en otra) */
const isOk = (codigo) => Number(codigo) === 1;

/* ---------------------------------------------------------------------------
   ADAPTADOR DE RESPUESTA — UNICO PUNTO A AJUSTAR cuando backend confirme el
   contrato de /endoso/cancelacion-masiva/previo/{idSolicitud}.
   El PDF de la HU8 solo define las rutas, no las estructuras de respuesta, asi
   que se aceptan los nombres de campo mas probables. Ver
   docs/HU8_cancelaciones_masivas_guia_agente_JS.md seccion 19.
   --------------------------------------------------------------------------- */
const firstArray = (...candidatos) =>
  candidatos.find((valor) => Array.isArray(valor)) ?? [];

/* CONFIRMADO contra QA 2026-09-14: la respuesta real es
   { codigo: "1", mensaje: null, registros: [...], errores: [...] }
   Los alias se conservan por si backend renombra. */
export const getPolizasOk = (data) =>
  firstArray(
    data?.registros,
    data?.polizas,
    data?.polizasCanceladas,
    data?.cancelaciones
  );

export const getPolizasError = (data) =>
  firstArray(data?.errores, data?.polizasConError, data?.polizasNoCanceladas);

/* el id lo genera el front: el mockup de la HU8 muestra la misma poliza
   repetida en ambas tablas, y DataGrid v4 tira error por ids duplicados */
export const mapPolizaOk = (row, index) => ({
  ...row,
  id: `ok-${row?.poliza ?? "sin-poliza"}-${index}`,
  oficina: row?.oficina ?? "",
  ramo: row?.ramo ?? "",
  poliza: row?.poliza ?? row?.numeroPoliza ?? "",
  fechaCancelacion: row?.fechaCancelacion ?? row?.fecha ?? "",
});

export const mapPolizaError = (row, index) => ({
  ...row,
  id: `err-${row?.poliza ?? "sin-poliza"}-${index}`,
  oficina: row?.oficina ?? "",
  ramo: row?.ramo ?? "",
  poliza: row?.poliza ?? row?.numeroPoliza ?? "",
  /* el error viaja como mensaje en el upload de altas; se aceptan ambos */
  error: row?.error ?? row?.mensaje ?? row?.descripcionError ?? "",
});
/* ------------------------------------------------------------------------- */

/* Logica pura para poder testearla sin montar el contexto. El hook de abajo
   solo inyecta las dependencias. */
export const rulesByFiftyFive = async ({
  file,
  formDta,
  endoso,
  createMessage,
  setShowButtons,
  showButtons,
  setPolizasCanceladas,
  setPolizasConError,
  setResumenListo,
  resetResumen,
}) => {
  if (!file) {
    createMessage({ text: errorText.sinArchivo, variant: "warning" });
    return;
  }

  /* rulesBy-16 no normaliza a minusculas y ARCHIVO.XLSX le falla */
  const extension = file.name.split(".").pop()?.toLowerCase();
  if (extension !== "xlsx") {
    createMessage({ text: errorText.invalidType, variant: "warning" });
    return;
  }

  resetResumen();

  const uploadRes = await endoso.uploadCancelacionMasiva(
    file,
    formDta.noSolicitud
  );
  if (!isOk(uploadRes?.data?.codigo)) {
    createMessage({
      text: uploadRes?.data?.mensaje || errorText.upload,
      variant: "warning",
    });
    return;
  }

  const previoRes = await endoso.previoCancelacionMasiva(
    file,
    formDta.noSolicitud
  );
  if (!isOk(previoRes?.data?.codigo)) {
    createMessage({
      text: previoRes?.data?.mensaje || errorText.previo,
      variant: "warning",
    });
    return;
  }

  setPolizasCanceladas(getPolizasOk(previoRes.data).map(mapPolizaOk));
  setPolizasConError(getPolizasError(previoRes.data).map(mapPolizaError));
  setResumenListo(true);
  setShowButtons({ ...showButtons, multiTable: true });
};

/* ---------------------------------------------------------------------------
   PASO 4 — Ejecutar la cancelacion masiva
   --------------------------------------------------------------------------- */

/* Correo de quien ejecuta el lote. El contrato pide obtenerlo igual que el
   endoso 5: localStorage.token.username. Se duplica el helper de
   footerSection:getUsernameFromToken en vez de extraerlo a shared (ADR-001:
   la duplicacion entre tipos es deuda aceptada; extraerlo obligaria a tocar el
   footer, compartido con todos los tipos). */
export const getCorreoAgente = () => {
  try {
    const tokenData = localStorage.getItem("token");
    if (!tokenData) return null;
    return JSON.parse(tokenData).username || null;
  } catch {
    return null;
  }
};

/* El contrato exige mandar SOLO las filas validas y SOLO estos cuatro campos:
   las filas del resumen traen ademas `id` (generado por el front para
   DataGrid), `estado` y `mensaje`, que no deben viajar. */
export const buildFilasEjecucion = (polizasCanceladas = []) =>
  polizasCanceladas
    .filter((row) => {
      const estado = String(row?.estado ?? "").toUpperCase();
      /* si el backend dejara de mandar `estado`, no se descarta la fila: viene
         de `registros`, que por contrato ya son solo las validas */
      return estado === "" || estado === "VALIDA";
    })
    .map(({ oficina, ramo, poliza, fechaCancelacion }) => ({
      oficina,
      ramo,
      poliza,
      fechaCancelacion,
    }));

/* Motivo de cancelacion con el que se guarda cada endoso del lote (HU8). */
export const MOTIVO_CANCELACION_MASIVA = "27";

/* Usuario del portal para poliza/bloquea: el resto de tipos lo toma de
   AuthContext.token.cdUsuario, que es lo mismo que guarda localStorage.token. */
export const getCdUsuario = () => {
  try {
    const tokenData = localStorage.getItem("token");
    if (!tokenData) return null;
    return JSON.parse(tokenData).cdUsuario || null;
  } catch {
    return null;
  }
};

/* Mismo cifrado que footerSection:encryptUrl para enviar_documentos (ADR-001:
   se duplica en vez de extraerlo del footer, compartido con todos los tipos). */
export const encryptUrl = (url, key = process.env.REACT_APP_URL_SECRET_KEY) =>
  CryptoJS.AES.encrypt(url, CryptoJS.enc.Utf8.parse(key), {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7,
  }).toString();

/* bloquea espera dd/MM/yyyy; si el previo llegara en ISO se convierte. */
export const toFechaBloquea = (fecha) => {
  const iso = String(fecha ?? "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return iso ? `${iso[3]}/${iso[2]}/${iso[1]}` : fecha;
};

const esMismaPoliza = (a, b) =>
  String(a?.oficina) === String(b?.oficina) &&
  String(a?.ramo) === String(b?.ramo) &&
  String(a?.poliza) === String(b?.poliza);

const getMensajeError = (res, fallback) =>
  res?.data?.error?.[0]?.desError ||
  res?.data?.mensaje ||
  res?.error?.mensaje ||
  fallback;

/* guarda -> confirma -> enviar_documentos de UNA poliza ya bloqueada. Un fallo
   en enviar_documentos no revierte la cancelacion: se reporta aparte. */
export const cancelarPoliza = async ({
  endoso,
  poliza,
  solicitud,
  suplemento,
  data,
  correoAgente,
  encrypt = encryptUrl,
}) => {
  const guardaRes = await endoso.saveEndoso({
    nivel: 1,
    orden: 1,
    solicitud,
    valor: MOTIVO_CANCELACION_MASIVA,
  });
  if (!isOk(guardaRes?.data?.codigo))
    return { ok: false, error: getMensajeError(guardaRes, errorText.guarda) };

  const confirmaRes = await endoso.confirma({
    idSolicitud: solicitud,
    oficina: poliza.oficina,
    poliza: poliza.poliza,
    ramo: poliza.ramo,
    suplemento,
    cdTipo: "PE",
  });
  if (!isOk(confirmaRes?.data?.codigo))
    return {
      ok: false,
      error: getMensajeError(confirmaRes, errorText.confirma),
    };

  const documentos = confirmaRes.data?.documentos ?? [];
  const correoContratante = confirmaRes.data?.correoContratante;
  let correoEnviado = false;
  if (correoContratante) {
    const envioRes = await endoso.sendDocumentsEmail({
      idSolicitud: solicitud,
      oficina: poliza.oficina,
      poliza: poliza.poliza,
      ramo: poliza.ramo,
      suplemento,
      codigoEndoso: data.tipend,
      descripcionEndoso: data.descripcionEndoso,
      correoAgente,
      correoContratante,
      documentos: documentos.map((doc) => ({ ...doc, url: encrypt(doc.url) })),
    });
    correoEnviado = isOk(envioRes?.data?.codigo);
  }
  return { ok: true, documentos, correoEnviado };
};

/* La tabla de documentos usa doc.id como id de fila: al juntar varias polizas
   se prefija con la poliza para que no se repitan. */
const etiquetarDocumentos = (poliza, documentos = []) =>
  documentos.map((doc, index) => ({
    ...doc,
    id: `${poliza.ramo}-${poliza.poliza}-${doc.id ?? index}`,
    nombre: `${poliza.poliza} - ${doc.nombre ?? "Documento"}`,
  }));

/* Ejecuta el lote: por cada poliza de la tabla bloquea -> guarda -> confirma
   -> enviar_documentos, y al final la poliza que origina el endoso (ya
   bloqueada al elegir el tipo) guarda -> confirma -> enviar_documentos. Si una
   poliza falla se sigue con las demas y pasa a la tabla de errores. */
export const ejecutarCancelacion55 = async ({
  polizasCanceladas,
  polizasConError = [],
  formDta,
  data,
  endoso,
  searchPoliza,
  correoAgente,
  cdUsuario,
  notificar,
  setPolizasCanceladas,
  setPolizasConError,
  encrypt,
}) => {
  const origen = {
    oficina: data.oficina,
    ramo: data.ramo,
    poliza: data.poliza,
  };
  /* la poliza origen se procesa una sola vez, aunque venga en el archivo */
  const filas = buildFilasEjecucion(polizasCanceladas).filter(
    (fila) => !esMismaPoliza(fila, origen)
  );
  if (!filas.length) {
    notificar({ text: errorText.sinFilas, severity: "warning" });
    return null;
  }

  const canceladas = [];
  const fallidas = [];
  let documentos = [];
  let sinCorreo = 0;

  const registrar = (fila, resultado) => {
    if (!resultado.ok) {
      fallidas.push({ ...fila, error: resultado.error });
      return;
    }
    canceladas.push(fila);
    documentos = documentos.concat(
      etiquetarDocumentos(fila, resultado.documentos)
    );
    if (!resultado.correoEnviado) sinCorreo += 1;
  };

  for (let index = 0; index < filas.length; index++) {
    const fila = filas[index];
    notificar({
      text: `Procesando póliza ${index + 1} de ${filas.length}...`,
      severity: "info",
    });
    try {
      const bloqueaRes = await searchPoliza.bloquea({
        oficina: fila.oficina,
        ramo: fila.ramo,
        poliza: fila.poliza,
        fechainicial: toFechaBloquea(fila.fechaCancelacion),
        tipend: data.tipend,
        descripcionEndoso: data.descripcionEndoso,
        usuario: cdUsuario,
      });
      if (
        !isOk(bloqueaRes?.data?.codigo) ||
        bloqueaRes.data?.error?.[0]?.desError
      ) {
        registrar(fila, {
          ok: false,
          error: getMensajeError(bloqueaRes, errorText.bloquea),
        });
        continue;
      }
      registrar(
        fila,
        await cancelarPoliza({
          endoso,
          poliza: fila,
          solicitud: bloqueaRes.data.nsolicitud,
          suplemento: bloqueaRes.data.suplemLogico,
          data,
          correoAgente,
          encrypt,
        })
      );
    } catch {
      registrar(fila, { ok: false, error: errorText.ejecutar });
    }
  }

  notificar({ text: "Procesando póliza origen...", severity: "info" });
  let resultadoOrigen;
  try {
    resultadoOrigen = await cancelarPoliza({
      endoso,
      poliza: origen,
      solicitud: formDta.noSolicitud,
      suplemento: formDta.suplemLogico,
      data,
      correoAgente,
      encrypt,
    });
  } catch {
    resultadoOrigen = { ok: false, error: errorText.ejecutar };
  }
  if (resultadoOrigen.ok) {
    documentos = etiquetarDocumentos(origen, resultadoOrigen.documentos).concat(
      documentos
    );
    if (!resultadoOrigen.correoEnviado) sinCorreo += 1;
  }

  /* Se repintan las tablas con el resultado REAL: los errores del previo se
     conservan y se suman los de la ejecucion. */
  setPolizasCanceladas(canceladas.map(mapPolizaOk));
  setPolizasConError([
    ...polizasConError,
    ...fallidas.map((fila, index) =>
      mapPolizaError(fila, polizasConError.length + index)
    ),
  ]);

  const avisos = [];
  if (fallidas.length) avisos.push(`${fallidas.length} con error`);
  if (!resultadoOrigen.ok)
    avisos.push(`la póliza origen no se canceló (${resultadoOrigen.error})`);
  if (sinCorreo)
    avisos.push(`no se pudo enviar el correo de ${sinCorreo} póliza(s)`);

  notificar({
    text: `Cancelación masiva: ${canceladas.length} póliza(s) cancelada(s)${
      resultadoOrigen.ok ? " y la póliza origen" : ""
    }${avisos.length ? `; ${avisos.join("; ")}` : ""}.`,
    severity: avisos.length ? "warning" : "success",
  });

  return { documentos, canceladas, fallidas, origen: resultadoOrigen };
};

/* La vista de caratula del proyecto (FormTable check type={2}, la misma que
   usa el endoso de cancelacion) recibe { documentos: [{ id, nombre, url }] }.
   Solo se arma si alguna poliza se cancelo y devolvio documentos. */
export const toConfirmDataCaratula = (documentos) =>
  documentos?.length ? { documentos } : null;

export const useRulesBy55 = () => {
  const {
    file,
    polizasCanceladas,
    setPolizasCanceladas,
    polizasConError,
    setPolizasConError,
    setResumenListo,
    resetResumen,
  } = useContext(CancelacionMasivaContext);

  const handleGuardarBorrador55 = async (
    data,
    createMessage,
    formDta,
    setShowButtons,
    showButtons
  ) =>
    rulesByFiftyFive({
      file,
      formDta,
      endoso: endosoService,
      createMessage,
      setShowButtons,
      showButtons,
      setPolizasCanceladas,
      setPolizasConError,
      setResumenListo,
      resetResumen,
    });

  const handleEjecutarCancelacion55 = async (formDta, data, notificar) =>
    ejecutarCancelacion55({
      polizasCanceladas,
      polizasConError,
      formDta,
      data,
      endoso: endosoService,
      searchPoliza: searchPolizaService,
      correoAgente: getCorreoAgente(),
      cdUsuario: getCdUsuario(),
      notificar,
      setPolizasCanceladas,
      setPolizasConError,
    });

  return { handleGuardarBorrador55, handleEjecutarCancelacion55 };
};
